import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Alert, Button, Card, Checkbox, Space, Table, Tag, Typography } from 'antd'
import dayjs from 'dayjs'
import api from '../../utils/api'

const { Text } = Typography
const lines = [{ value:'software', label:'ธุรกิจซอฟต์แวร์' }, { value:'sign_printing', label:'ธุรกิจร้านป้าย' }]
const money = value => Number(value || 0).toLocaleString('th-TH', { minimumFractionDigits:2, maximumFractionDigits:2 })
const date = value => dayjs(value).format('DD/MM/YYYY')
const label = (options, value) => options.find(option => option.value === value)?.label || 'ยังไม่ระบุ'
const errorText = error => error.response?.data?.message || 'โหลดหรือบันทึกข้อมูลไม่สำเร็จ'

export default function InvestorDocumentReview({ month, refreshKey, onVerifyLegacy }) {
  const [data, setData] = useState({ receipts:[], expenses:[], legacyPayments:[] })
  const [loading, setLoading] = useState(false), [loadError, setLoadError] = useState('')
  const [onlyPending, setOnlyPending] = useState(true)
  const loadVersion = useRef(0)
  const monthValue = month.format('YYYY-MM')
  const load = useCallback(async () => {
    const version = ++loadVersion.current
    setLoading(true); setLoadError('')
    try {
      const response = await api.get('/api/investors/admin/classification', { params:{ month:monthValue } })
      if (version !== loadVersion.current) return
      setData(response.data.data)
    } catch (error) { if (version === loadVersion.current) setLoadError(errorText(error)) }
    finally { if (version === loadVersion.current) setLoading(false) }
  }, [monthValue])
  useEffect(() => { load(); return () => { loadVersion.current++ } }, [load, refreshKey])
  const visible = rows => onlyPending ? rows.filter(row => row.needs_review) : rows
  const baseColumns = type => [
    { title:'เอกสาร', width:170, render:(_, row) => <Space direction="vertical" size={0}>
      <Text strong>{type === 'receipt' ? row.receipt_number || row.invoice_number : row.expense_number}</Text>
      <Text type="secondary">{date(type === 'receipt' ? row.receipt_date : row.expense_date)}</Text>
    </Space> },
    { title:'ธุรกิจ', width:220, render:(_, row) => <Space direction="vertical" size={2}>
      <Tag color={row.business_line ? 'blue' : 'orange'}>{label([...lines,{value:'shared',label:'ค่าใช้จ่ายร่วม'}],row.business_line)}</Tag>
      {row.suggested_business_line && <Text type="secondary">{type === 'receipt' ? 'จากชุดบัญชีเดิม' : 'ชุดบัญชีเดิม: ' + label(lines,row.suggested_business_line)}</Text>}
    </Space> }
  ]
  const receiptPending = data.receipts.filter(row => row.needs_review).length
  const expensePending = data.expenses.filter(row => row.needs_review).length

  return <Space direction="vertical" size="middle" style={{width:'100%'}}>
    <Alert type="info" showIcon message={`เตรียมข้อมูลปิดงวด ${month.format('MM/YYYY')}`}
      description="ระบบใช้ประเภทธุรกิจจากชุดบัญชีเดิม และใช้รายการบัญชีที่ลงแล้วแยกต้นทุนขายกับค่าใช้จ่ายให้อัตโนมัติ รายการที่ข้อมูลบัญชีไม่ครบจะแสดงเฉพาะรายการนั้นให้ตรวจ" />
    <Space wrap>
      <Tag color={receiptPending ? 'orange' : 'green'}>ใบเสร็จรอตรวจ {receiptPending} ใบ</Tag>
      <Tag color={expensePending ? 'orange' : 'green'}>ค่าใช้จ่ายรอตรวจ {expensePending} ใบ</Tag>
      <Checkbox checked={onlyPending} onChange={event => setOnlyPending(event.target.checked)}>แสดงเฉพาะที่ต้องแก้ไข</Checkbox>
      <Button onClick={load} loading={loading}>รีเฟรช</Button>
    </Space>
    {loadError && <Alert type="error" showIcon message={loadError} />}
    <Card title="ใบเสร็จ: ธุรกิจจากชุดบัญชีเดิม">
      <Table rowKey="id" loading={loading} dataSource={visible(data.receipts)} scroll={{x:900}} pagination={{pageSize:10}}
        columns={[...baseColumns('receipt'),
          {title:'ต้นทุนทั้งบิล',width:210,render:(_,row) => row.needs_cost_review
            ? <a href="/products">ตั้งต้นทุนในสินค้า</a>
            : <Space direction="vertical" size={0}><Text>{money(row.direct_cost)} บาท</Text>
              <Text type="secondary">{row.cost_source === 'estimated_product_cost' ? 'ประมาณจากต้นทุนสินค้า' : 'ตามต้นทุนที่บันทึกไว้'}</Text>
            </Space>},
          {title:'สถานะ',render:(_,row) => row.accounting_status !== 'posted'
            ? <a href="/accounting/bulk-posting">รอลงบัญชี</a>
            : !row.business_line ? <a href="/receipts">ตรวจชุดบัญชีที่ใบเสร็จ</a> : 'พร้อมคำนวณ'}
        ]} />
    </Card>
    <Card title="ค่าใช้จ่าย: คำนวณตามบัญชี GL">
      <Table rowKey="id" loading={loading} dataSource={visible(data.expenses)} scroll={{x:950}} pagination={{pageSize:10}}
        columns={[...baseColumns('expense'),{title:'ยอดก่อน VAT',dataIndex:'amount',align:'right',render:money},
          {title:'ค่าใช้จ่ายที่หัก',dataIndex:'operating_amount',align:'right',render:money},
          {title:'ต้นทุนขายจากบัญชี',dataIndex:'bill_cost_amount',align:'right',render:money},
          {title:'สถานะ',render:(_,row) => !row.business_line
            ? <a href="/expenses">ตรวจชุดบัญชีที่ค่าใช้จ่าย</a>
            : row.cost_evidence_error
              ? <a href="/accounting/bulk-posting">{row.cost_evidence_error}</a>
              : <Tag color="green">คำนวณจาก GL</Tag>}
        ]} />
    </Card>
    {!!data.legacyPayments.length && <Card title="ยืนยันวันรับชำระจากข้อมูลเดิม"><Table rowKey="id" dataSource={data.legacyPayments} scroll={{x:650}}
      columns={[{title:'บิล',render:(_,row) => row.receipt_number || row.invoice_number},
        {title:'วันประมาณการ',dataIndex:'payment_date',render:date},{title:'จำนวน',dataIndex:'amount',render:money},
        {title:'ดำเนินการ',render:(_,row) => <Button onClick={() => onVerifyLegacy(row)}>ยืนยันวันจริง</Button>}]} /></Card>}
  </Space>
}
